import React from "react";
import { Button, Modal } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";

// Iter v34{aa} — Build a human-readable qty string per SJ, grouped by unit.
// Example: items=[{qty:6,unit:"m³"},{qty:1,unit:"m³"},{qty:50,unit:"sak"}] → "7 m³ + 50 sak"
function summarizeQty(sj) {
  const items = sj?.items || [];
  if (!items.length) return "—";
  const byUnit = {};
  for (const it of items) {
    const u = (it.unit || "").trim() || "—";
    const q = Number(it.quantity || 0) || 0;
    byUnit[u] = (byUnit[u] || 0) + q;
  }
  const fmt = (n) => Number.isInteger(n) ? String(n) : Number(n.toFixed(2)).toString();
  return Object.entries(byUnit).map(([u, q]) => `${fmt(q)} ${u}`).join(" + ");
}

// Iter v34az — Summarize material names from SJ items (dedup + concat).
function summarizeMaterials(sj) {
  const items = sj?.items || [];
  if (!items.length) return "—";
  const names = [];
  for (const it of items) {
    const n = (it.material_name || "").trim();
    if (n && !names.includes(n)) names.push(n);
  }
  if (!names.length) return "—";
  if (names.length === 1) return names[0];
  const first = names[0];
  return `${first} +${names.length - 1}`;
}

/**
 * SJ Picker Modal — used by the Billing wizard Step 2 to pull Surat Jalan
 * documents into the current invoice. Pure presentational: all state is
 * passed in from the parent.
 */
export function SjPickerModal({
  open,
  onClose,
  cfg,
  pos,
  source,
  selection,
  setSelection,
  filterPo,
  setFilterPo,
  onConfirm,
}) {
  return (
    <Modal open={open} onClose={onClose} title="Tarik Surat Jalan" size="xl">
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-stone-500">
            Filter:{" "}
            {cfg.po_id && filterPo ? (
              <>PO <b>{pos.find((p) => p.id === cfg.po_id)?.po_number}</b></>
            ) : cfg.client_name ? (
              <>Klien <b>{cfg.client_name}</b></>
            ) : (
              <i>tidak ada filter</i>
            )}
            . Semua SJ ditampilkan (termasuk yang sudah ditagih) selama masih sesuai filter.
          </p>
          {cfg.po_id && (
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <input
                type="checkbox"
                checked={filterPo}
                onChange={(e) => setFilterPo(e.target.checked)}
                data-testid="picker-filter-po"
              />
              Filter hanya PO terpilih
            </label>
          )}
        </div>
        <div className="border border-stone-200 max-h-80 overflow-y-auto">
          {source.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-stone-400">Tidak ada Surat Jalan tersedia.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600 border-b border-stone-200 sticky top-0">
                  <th className="py-2 px-3 w-8">
                    <input
                      type="checkbox"
                      checked={source.length > 0 && source.every((s) => selection.includes(s.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const all = source.map((s) => s.id);
                          setSelection((prev) => [...new Set([...prev, ...all])]);
                        } else {
                          const sourceIds = new Set(source.map((s) => s.id));
                          setSelection((prev) => prev.filter((x) => !sourceIds.has(x)));
                        }
                      }}
                      data-testid="picker-select-all"
                      title="Pilih semua"
                    />
                  </th>
                  <th className="py-2 px-3">No SJ</th>
                  <th className="py-2 px-3">Tanggal</th>
                  <th className="py-2 px-3">Material</th>
                  <th className="py-2 px-3">Kendaraan</th>
                  <th className="py-2 px-3">Tujuan</th>
                  <th className="py-2 px-3 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {source.map((s, idx) => (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`picker-row-${idx}`}>
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={selection.includes(s.id)}
                        onChange={(e) => {
                          setSelection((prev) => (e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)));
                        }}
                        data-testid={`picker-check-${idx}`}
                      />
                    </td>
                    <td className="py-2 px-3 font-mono font-medium">{s.sj_number}</td>
                    <td className="py-2 px-3 font-mono text-stone-600">{formatDate(s.date)}</td>
                    <td className="py-2 px-3 text-stone-700" title={(s.items || []).map(i => i.material_name).filter(Boolean).join(", ")} data-testid={`picker-material-${idx}`}>{summarizeMaterials(s)}</td>
                    <td className="py-2 px-3 font-mono text-stone-600">{s.vehicle_no || "—"}</td>
                    <td className="py-2 px-3 text-stone-600">{s.delivery_location || "—"}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-stone-700 whitespace-nowrap" data-testid={`picker-qty-${idx}`}>{summarizeQty(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-stone-200">
          <div className="flex items-center gap-3">
            <span className="text-xs text-stone-500">{selection.length} dipilih</span>
            <Button
              variant="ghost"
              className="!py-1 !px-2 text-xs"
              onClick={() => setSelection(source.map((s) => s.id))}
              data-testid="picker-select-all-btn"
            >
              Pilih Semua ({source.length})
            </Button>
            <Button
              variant="ghost"
              className="!py-1 !px-2 text-xs"
              onClick={() => setSelection([])}
              data-testid="picker-clear-btn"
            >
              Bersihkan
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Batal</Button>
            <Button onClick={onConfirm} disabled={selection.length === 0} data-testid="picker-confirm-btn">
              Tarik {selection.length > 0 ? `(${selection.length})` : ""}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
